import { Box, Tooltip } from '@mui/material';
import axios from 'axios';

import React, { useState, useCallback, useMemo } from 'react';
import { formatDate } from 'utils/helpers/dates';
import { ReactComponent as DeleteIcon } from 'assets/icons/DeleteIcon.svg';
import { ReactComponent as ResendIcon } from 'assets/icons/resend.svg';
import { ReactComponent as MainContactOutlinedIcon } from 'assets/icons/MainContactOutlined.svg';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { useBaoSelector } from 'utils/hooks/redux';
import { SUPER_ADMIN } from 'utils/constants/roles';

import { ICompanies, ICompany } from './types';
import { CaptchaModal } from 'molecules/CaptchaModal/CaptchaModal';
import ContactIcon from 'atoms/ContactIcon';
import { UserRow } from 'organisms/CompanyDetailsCard/CompanyContacts';
import Table from 'molecules/Table';
import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import Link from 'atoms/Link/Link';
import ButtonMenu from 'molecules/ButtonMenu';
import { neutrals } from 'utils/styles/color';
import { UserCompany } from 'store/user/userSlice';
import { palette } from 'utils/styles/variables';
import DeleteContactModal from './components/DeleteContactModal';
import { ReactComponent as CurrencyIcon } from 'assets/icons/currency.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/broken-link.svg';
import dayjs from 'dayjs';

export const getContactDotsOptions = (
  isMain: boolean | null,
  user?: UserRow
) => {
  const options = [
    {
      icon: <DeleteIcon color={neutrals[700]} />,
      label: 'Delete contact',
      value: 'delete',
    },
  ];

  // if user didn't log in yet, then show resend invite option
  // if we resent invitation more than 24 hours ago, then show resend invite option
  if (!user?.last_logged_time) {
    const lastResentInviteDate = dayjs(user?.last_resent_invite || undefined);
    const has24HoursPassed = dayjs().diff(lastResentInviteDate, 'hour') >= 24;

    if (!user?.last_resent_invite || has24HoursPassed) {
      options.unshift({
        icon: <ResendIcon color={neutrals[700]} />,
        label: 'Resend invitation',
        value: 'invite',
      });
    }
  }

  if (isMain === null) return options;

  if (!isMain) {
    return [
      {
        icon: <MainContactOutlinedIcon color={neutrals[700]} />,
        label: 'Designate as main contact',
        value: 'main',
      },
      ...options,
    ];
  }

  return options;
};

const Companies = ({
  order,
  orderBy,
  onRequestSort,
  companiesRow,
  totalCount,
  users,
  update,
  setUpdate,
  setLimit,
  setPage,
  page,
  loading,
}: ICompanies) => {
  const { value: user } = useBaoSelector((state) => state.user);

  const isSuperAdmin = user.userType === SUPER_ADMIN;

  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [, setRowsPage] = useState<number>(page || 0);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<ICompany>(companiesRow[0]);
  const [showDeleteCaptcha, setShowDeleteCaptcha] = useState<boolean>(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setRowsPage(newPage);
    if (setPage) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (option: number) => {
    setRowsPerPage(option);
    if (setLimit && setPage) {
      setPage(0);
      setLimit(option);
    }
  };

  const showDeleteCaptchaDialog = useCallback(() => {
    setShowDeleteCaptcha(true);
    setDeleteDialog(false);
  }, []);

  const hideDeleteCaptcha = useCallback(() => {
    setShowDeleteCaptcha(false);
    setDeleteDialog(false);
  }, []);

  const deleteMethod = useCallback(() => {
    hideDeleteCaptcha();

    axios
      .delete(`${process.env.REACT_APP_USER_SERVICE}/delete/${deletingId.id}`)
      .then(() => {
        if (update && setUpdate) {
          const number = update + 1;
          setUpdate(number);
          setDeleteDialog(false);
        }
      });
  }, [update, setUpdate, hideDeleteCaptcha, deletingId]);

  const handleInvitationResend = useCallback(
    async (row: UserRow) => {
      try {
        if (row.id) {
          await axios.get(
            `${process.env.REACT_APP_USER_SERVICE}/resend/invite`,
            {
              params: {
                user_id: row.id,
              },
            }
          );

          if (update && setUpdate) {
            const number = update + 1;
            setUpdate(number);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e.message);
      }
    },
    [update, setUpdate]
  );

  const deleteModal = (row: ICompany) => {
    setDeleteDialog(true);
    setDeletingId(row);
  };

  const handleSelect = (row: ICompany | UserRow, key: string) => {
    if (key === 'delete') {
      deleteModal(row as ICompany);
    }

    if (key === 'invite') {
      handleInvitationResend(row as UserRow);
    }
  };

  const columns = useMemo(() => {
    if (users) {
      return [
        {
          key: 'name',
          label: 'Full name',
          sortable: true,
          width: '20%',
          render: ({ name, surname }: UserRow) => (
            <StyledCellText>
              {name} {surname}
            </StyledCellText>
          ),
        },
        {
          key: 'email',
          label: 'Email',
          sortable: false,
          width: '20%',
          render: ({ email }: UserRow) => (
            <StyledCellText>{email}</StyledCellText>
          ),
        },
        {
          key: 'company',
          label: 'Company',
          sortable: false,
          width: '25%',
          render: ({ company }: { company: UserCompany[] }) =>
            company?.map(({ name, company_hs_id }: UserCompany) => (
              <StyledCellContainer>
                <Link to={`/company/${company_hs_id}`} $textDecoration="none">
                  <StyledCellText $textType="link">{name}</StyledCellText>
                </Link>
              </StyledCellContainer>
            )) || '',
        },
        {
          key: 'logged',
          label: 'Last logged',
          sortable: false,
          width: '20%',
          render: ({ last_logged_time }: UserRow) => (
            <StyledCellContainer>
              <StyledCellText>
                {last_logged_time && formatDate(last_logged_time, { time: '' })}
              </StyledCellText>
            </StyledCellContainer>
          ),
        },
        {
          key: 'actions',
          label: '',
          sortable: false,
          width: '15%',
          render: (row: UserRow | ICompany) => (
            <StyledCellContainer $right>
              {isSuperAdmin ? (
                <StyledCellContainer gap="24px !important" $hStack>
                  <ButtonMenu
                    options={getContactDotsOptions(null, row as UserRow)}
                    onSelect={(k: string) => handleSelect(row as ICompany, k)}
                  />
                </StyledCellContainer>
              ) : null}
            </StyledCellContainer>
          ),
        },
      ];
    }

    return [
      {
        key: 'company',
        label: 'Company name',
        sortable: false,
        width: '30%',
        render: ({ company_hs_id, name }: ICompany) => (
          <Link to={`/company/${company_hs_id}`} $textDecoration="none">
            <StyledCellText $textType="link">{name}</StyledCellText>
          </Link>
        ),
      },
      {
        key: 'invoiceQbNumber',
        label: 'Paid invoices',
        greyText: 'Number of invoices',
        sortable: false,
        width: '18%',
        render: ({ billing_info, currency }: ICompany) =>
          currency && (
            <StyledCellContainer $vStack $pr="40px" $right>
              <StyledCellText>
                {currencyFormatter(
                  billing_info ? billing_info.paid_invoices_total_amount : 0,
                  currency
                )}
              </StyledCellText>
              <StyledCellText $textType="grey">
                Invoices:{' '}
                {billing_info ? billing_info.paid_invoices_total_count : 0}
              </StyledCellText>
            </StyledCellContainer>
          ),
      },
      {
        key: 'invoices',
        label: 'Outstanding invoices',
        greyText: 'Number of invoices',
        sortable: false,
        width: '20%',
        render: ({ billing_info, currency }: ICompany) =>
          currency && (
            <StyledCellContainer $vStack $pr="40px" $right>
              <StyledCellText>
                {currencyFormatter(
                  billing_info
                    ? billing_info.outstanding_invoices_total_amount
                    : 0,
                  currency
                )}
              </StyledCellText>
              <StyledCellText $textType="grey">
                Invoices:{' '}
                {billing_info
                  ? billing_info.outstanding_invoices_total_count
                  : 0}
              </StyledCellText>
            </StyledCellContainer>
          ),
      },
      {
        key: 'autopayment',
        label: 'Auto payment',
        sortable: false,
        width: '12%',
        render: ({
          is_auto_payment,
          main_payment_method,
          currency,
        }: ICompany) =>
          currency && (
            <StyledCellContainer $vStack $disabled>
              <StyledCellText
                $color={is_auto_payment ? undefined : palette.error[400]}
              >
                {is_auto_payment ? 'Enabled' : 'Disabled'}
              </StyledCellText>
              {is_auto_payment && (
                <StyledCellText $textType="grey">
                  {getPaymentMethod(main_payment_method)}
                </StyledCellText>
              )}
            </StyledCellContainer>
          ),
      },
      {
        key: 'currency',
        label: 'Currency',
        sortable: false,
        width: '10%',
        render: ({ currency, qb_region }: ICompany) =>
          currency ? (
            <StyledCellContainer $vStack $disabled>
              <StyledCellText>{currency}</StyledCellText>
              <StyledCellText $textType="grey">
                {getQbRegion(qb_region)}
              </StyledCellText>
            </StyledCellContainer>
          ) : (
            <CurrencyIcon color={palette.error[500]} />
          ),
      },
      {
        key: 'actions',
        label: '',
        greyText: '',
        sortable: false,
        width: '10%',
        render: ({ users_count, sync_with_zconnect }: ICompany) => (
          <StyledCellContainer $hStack $right>
            {!sync_with_zconnect && (
              <Tooltip title="Broken integration with ZConnect">
                <Box>
                  <LinkIcon color={palette.error[400]} />
                </Box>
              </Tooltip>
            )}
            <Box>
              <ContactIcon count={users_count} />
            </Box>
          </StyledCellContainer>
        ),
      },
    ];
  }, []);

  function getPaymentMethod(method?: string) {
    if (method === 'sepa_debit') return 'SEPA';
    return method === 'ach_debit' ? 'ACH' : 'Credit card';
  }

  function getQbRegion(qbRegion?: string) {
    if (!qbRegion) return '';
    if (qbRegion.toLowerCase() === 'usa') return 'US QuickBooks';
    if (qbRegion.toLowerCase() === 'europe') return 'European QuickBooks';
    if (qbRegion.toLowerCase() === 'india') return 'Zohobooks';
    return '';
  }

  return (
    <>
      <StyledTableContainer>
        <Table
          order={order}
          orderBy={orderBy}
          onRequestSort={onRequestSort}
          loading={loading}
          data={companiesRow}
          columns={columns}
          idKey="company_hs_id"
          sx={{ minWidth: 650 }}
          aria-label="simple table"
        />

        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          ActionsComponent={(props) => (
            <PaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100, 200]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        />
      </StyledTableContainer>

      <DeleteContactModal
        open={deleteDialog}
        user={deletingId as unknown as UserRow}
        onSubmit={showDeleteCaptchaDialog}
        onClose={() => setDeleteDialog(false)}
      />

      {showDeleteCaptcha ? (
        <CaptchaModal onComplete={deleteMethod} onClose={hideDeleteCaptcha} />
      ) : (
        <span />
      )}
    </>
  );
};

export default Companies;
